'use client';

import { useEffect, useState } from 'react';

import {
  ArrowsRightLeftIcon,
  MapPinIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

import { BounceLoader } from 'react-spinners';
import {
  hydrateUserLocation,
  updateUserLocation,
} from '@/redux/slices/userLocation.slice';
import { GetOutletMenusForCustomer } from '@/services/menu.api';
import { GetOutletsAroundMe } from '@/services/order.api';
import { useDispatch, useSelector } from 'react-redux';
import LocationInput from '@/components/global/LocationInput';
import OrderType from '@/components/global/OrderType';
import Modal from '@/components/global/Modal';
import MenuItemCard from '@/components/menu/MenuItemCard';
import PageContainer from '@/components/global/page-container';
import AppStorage, { getUserLocation } from '@/utils/helpers';
import LocationDetails from '@/components/global/LocationDetails';
import LocationWarning from '@/components/global/LocationWarning';
import { useAppSelector } from '@/redux/hooks';

export default function OffersPage() {
  const [outlets, setOutlets] = useState([]);
  const [chosenOutlet, setChosenOutlet] = useState<any>({});
  const [promoMenu, setPromoMenu] = useState<any>([]);

  // State to control modal visibility and loading state
  const [viewModal, setViewModal] = useState({
    location: false,
    outlet: false,
  });

  const [loading, setLoading] = useState(false);

  const location = useSelector((state: any) => state.userLocation.location);
  const { orderType: orderCategory } = useAppSelector(
    (state: any) => state.checkout
  );
  const [orderType, setOrderType] = useState<string | null>(null);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<
    {
      name: string;
      city: string;
      location: { coordinates: [number, number] };
    }[]
  >([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  // useEffect to load AppStorage data
  useEffect(() => {
    const x = AppStorage.get('orderType') as string;
    setOrderType(x);
  }, []);

  // Redux dispatch function
  const dispatch = useDispatch();

  // Hydrate location from AppStorage on initial render
  useEffect(() => {
    const storedLocation = JSON.parse(AppStorage.get('location') as string);
    const storedOutlet = JSON.parse(AppStorage.get('outlet') as string);
    if (storedLocation) {
      dispatch(hydrateUserLocation(storedLocation));
    }

    if (storedOutlet) {
      setChosenOutlet(storedOutlet);
    }

    // if (!storedLocation) {
    //   setViewModal((prev) => ({ ...prev, location: true }));
    // }
  }, [dispatch]);

  // Fetch nearby outlets based on the location
  async function fetchNearbyOutlets(lat: number, lng: number) {
    setLoading(true);
    const res = await GetOutletsAroundMe(lat, lng);
    setOutlets(res);
    setChosenOutlet(res[0]);
    setLoading(false);
  }

  // Fetch menu content for a specific outlet
  async function fetchMenuContent(id: string) {
    const res = await GetOutletMenusForCustomer(id);
  }

  // Effect to fetch outlets and update global state when location or orderType changes
  useEffect(() => {
    if (location && orderType) {
      fetchNearbyOutlets(location.lat, location.lng);
      dispatch(updateUserLocation(location));
    }
  }, [location, orderType, dispatch]);

  // Effect to fetch menu content when chosen outlet changes
  useEffect(() => {
    if (chosenOutlet?._id) {
      fetchMenuContent(chosenOutlet._id);
      AppStorage.save('outlet', JSON.stringify(chosenOutlet));
    }
  }, [chosenOutlet]);

  // Loading state handling
  if (loading) {
    return (
      <div className="w-full py-40 flex justify-center items-center">
        <BounceLoader color="#ca1611" />
      </div>
    );
  }

  return (
    <PageContainer
      pageTitle="Our Offers"
      pageDesc="Check out our exciting offers, specially curated for you!"
    >
      <section className="w-full max-w-[1440px] mx-auto">
        {/* Modal for setting location and order type */}
        <Modal
          modalTitle="Start here"
          openModal={viewModal.location}
          closeModal={() =>
            setViewModal((prev) => ({ ...prev, location: false }))
          }
          shouldCloseOnOverlayClick={false}
          closeButton
        >
          {!location && (
            // <LocationInput />
            <LocationDetails
              setState={setState}
              state={state}
              setCity={setCity}
              city={city}
              setBranch={setBranch}
              branch={branch}
              setBranches={setBranches}
              setShowWarning={setShowWarning}
            />
          )}
          {!orderType && <OrderType />}
          <button
            disabled={!state || !city || !branch || !orderCategory}
            className={`text-white flex gap-2 items-center shadow px-5 py-3 w-max mx-auto rounded-full ${!state || !city || !branch || !orderCategory ? 'bg-gray-300' : 'bg-crunchies-red'}`}
            onClick={() => {
              const newLocation = JSON.parse(
                AppStorage.get('location') as string
              );
              dispatch(updateUserLocation(newLocation));
              setViewModal((prev) => ({ ...prev, location: false }));
            }}
          >
            <RocketLaunchIcon className="w-6" />
            <div>Let's go!</div>
          </button>
        </Modal>

        <Modal
          openModal={showWarning}
          closeModal={() => setShowWarning(false)}
          modalTitle=""
          closeButton={true}
          shouldCloseOnOverlayClick={true}
        >
          <LocationWarning />
        </Modal>

        {location && outlets.length === 0 ? (
          <div className="flex flex-col gap-10 items-center justify-center h-[70vh]">
            <p>Sorry, there are no nearby outlets.</p>
            <button
              className="text-white flex gap-2 items-center bg-crunchies-red shadow px-5 py-3 w-max mx-auto rounded-full"
              onClick={() => {
                AppStorage.remove('location');
                dispatch(updateUserLocation(null));
                setViewModal((prev) => ({ ...prev, location: true }));
              }}
            >
              <MapPinIcon className="w-6" />
              <div className="text-xs">Choose a different location</div>
            </button>
          </div>
        ) : (
          <>
            {/* Display nearest outlet information */}
            <div className="px-5 grid gap-5 md:flex items-center">
              <div>
                <p className="grid grid-cols-3 md:flex gap-2 text-xs">
                  <span className="text-gray-500">Ordering from:</span>
                  <span className="font-bold col-span-2">
                    {chosenOutlet?.name}
                  </span>
                </p>
                <p className="grid grid-cols-3 md:flex gap-2 text-xs">
                  <span className="text-gray-500">Outlet's address:</span>
                  <span className="col-span-2">{chosenOutlet?.address}</span>
                </p>
              </div>

              {/* Change outlet */}
              <div>
                <button
                  className="text-xs py-3 px-5 rounded-full bg-crunchies-red text-crunchies-white font-bold"
                  onClick={() =>
                    setViewModal((prev) => ({ ...prev, outlet: true }))
                  }
                >
                  Change Outlet
                </button>

                {/* Change outlet modal */}
                <Modal
                  modalTitle="Choose a different outlet"
                  closeModal={() =>
                    setViewModal((prev) => ({ ...prev, outlet: false }))
                  }
                  openModal={viewModal.outlet}
                  shouldCloseOnOverlayClick={true}
                >
                  {outlets?.map((outlet: any, index: number) => (
                    <button
                      className="shadow-lg text-left p-5 flex items-center justify-between gap-5"
                      key={index}
                      onClick={() => {
                        setChosenOutlet(outlet);
                        setViewModal((prev) => ({ ...prev, outlet: false }));
                      }}
                    >
                      <div>
                        <p className="font-bold">{outlet?.name}</p>
                        <p>{outlet?.address}</p>
                      </div>

                      <ArrowsRightLeftIcon className="w-6" />
                    </button>
                  ))}
                </Modal>
              </div>
            </div>

            {/* Menu items based on selected category */}
            {promoMenu.length ? (
              <div className="p-5 grid gap-5 grid-cols-2 md:gap-10 lg:grid-cols-4">
                {promoMenu?.map((item: any, index: number) => (
                  <MenuItemCard key={index} menuItem={item} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-10 items-center justify-center h-[70vh]">
                <p className="font-bold text-xl">
                  Our offers are coming soon...
                </p>
              </div>
            )}
          </>
        )}
      </section>
    </PageContainer>
  );
}
